import { NumberInput, Select } from "@msuite/ui/form";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import {
	type PostLoadCarrierTransactionJsonSchema,
	postLoadCarrierTransactionJsonSchema,
} from "@msuite/utils/schemas";
import { getTransactionTypeTranslationKey } from "@msuite/utils/stock";
import { transactionTypes } from "@msuite/utils/units";
import { FormProvider, useForm } from "react-hook-form";
import { client } from "@/hono";

/** Props Interface */
interface BookLoadCarrierTransactionModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	loadCarrierId: string;
}

/**
 * Book a carrier movement by hand: sixty pallets arrived, twelve came back, the yard count was
 * wrong by three.
 *
 * This is the only way a carrier balance changes outside a picking round, and it changes it by
 * adding to the ledger rather than by setting a number — so every balance in the yard has the
 * movements that explain it. `pick` is offered too, for the delivery that went out on a truck
 * before anybody opened the app.
 */
export function BookLoadCarrierTransactionModal({
	onClose,
	isOpen,
	onComplete,
	loadCarrierId,
}: BookLoadCarrierTransactionModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(postLoadCarrierTransactionJsonSchema),
		defaultValues: {
			operation: "ADD" as const,
			quantity: 1,
			type: "restock" as const,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: PostLoadCarrierTransactionJsonSchema) {
		const response = await client.stock["load-carriers"][":loadCarrierId"].transactions.$post({
			param: { loadCarrierId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.book_load_carrier_movement")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<div className="v-stack gap-4">
						<Select
							label={t("stock.operations.operation")}
							isRequired
							control={methods.control}
							name="operation"
							options={[
								{ label: t("stock.operations.add"), value: "ADD", item: null },
								{ label: t("stock.operations.remove"), value: "REMOVE", item: null },
							]}
						/>
						<NumberInput
							label={t("general.quantity")}
							isRequired
							control={methods.control}
							name="quantity"
							variant="integer"
							min={1}
							step={1}
						/>
						<Select
							label={t("stock.transaction_types.transaction_type")}
							isRequired
							control={methods.control}
							name="type"
							options={transactionTypes.map((type) => ({
								label: t(getTransactionTypeTranslationKey(type)),
								value: type,
								item: null,
							}))}
						/>
					</div>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
